import { useEffect, useMemo, useRef } from 'react'
import type { EmbedHost } from '@/features/booking'
import { directPath } from '../../../embed/protocol'
import type { HostBridge } from './bridge'

/**
 * The booking flow's view of the host bridge (Frontend §4.3, §4.4 step 1): when the visit may start and with which
 * embed origin, `step` / `close` / `closed`, and the panel's height (`resize`: a ResizeObserver on the panel root,
 * at most once per animation frame, panel layout only). Messages carry UI events only (Security §6.5).
 */
export function useEmbedHost(
  bridge: HostBridge,
  slug: string,
  source: string | null,
  preset: string | null = null,
): EmbedHost {
  const observer = useRef<ResizeObserver | null>(null)
  const raf = useRef(0)
  const shown = useRef<'panel' | 'overlay'>('panel')
  useEffect(
    () => () => {
      observer.current?.disconnect()
    },
    [],
  )
  return useMemo(() => {
    const report = (el: HTMLElement) => {
      cancelAnimationFrame(raf.current)
      raf.current = requestAnimationFrame(() => {
        if (shown.current !== 'panel' || !el.isConnected) return
        const height = Math.ceil(el.getBoundingClientRect().height)
        if (height > 0) bridge.post({ type: 'resize', height })
      })
    }
    return {
      start: async () => {
        await bridge.settled
        return bridge.embedOrigin()
      },
      layout: (next, booked) => {
        shown.current = next
        bridge.post(booked ? { type: 'step', layout: next, booked: true } : { type: 'step', layout: next })
      },
      requestClose: () => {
        bridge.post({ type: 'close' })
      },
      onHostClose: (listener) => bridge.onClose(listener),
      closed: () => {
        bridge.post({ type: 'closed' })
      },
      panelRef: (el) => {
        observer.current?.disconnect()
        observer.current = null
        if (!el) return
        if (typeof ResizeObserver !== 'undefined') {
          const ro = new ResizeObserver(() => {
            report(el)
          })
          ro.observe(el)
          observer.current = ro
        }
        report(el)
        // A size measured before the handshake had nowhere to go: send it once the host is known.
        void bridge.settled.then(() => {
          report(el)
        })
      },
      directLink: `${window.location.origin}${directPath(slug, source, preset)}`,
    }
  }, [bridge, slug, source, preset])
}
